import { z } from 'zod';
import { computeCostEur, modelPriceSchema } from './ai.js';
import { breakEvenListPrice, costPerPayingUser, DEFAULT_RC_FEE, marginAtPrice, netFactor, sensitivityGrid, sensitivityGridSchema, targetListPrice, } from './pricing.js';
/* ==========================================================================
 *  computeEconomics — the locked, pure, IO-free unit-economics contract
 *  (ADR-0011 §3.1–§3.5; generalises docs/09-metrics-and-monetization.md
 *  §"Unit economics").
 *
 *  This is the single headline entry point the admin pricing-decision view
 *  (Phase D, "D2") renders. It takes the FULL §3.4 input vector — every movable
 *  field editable — and returns every derived output: paying users, MRR, blended
 *  ARPU, blended cost/AU, total COGS, cost/paying user, break-even list price,
 *  target price @ margin, gross margin @ current price, operating profit,
 *  break-even MAU, the sensitivity grid, and an above/below indicator per target.
 *
 *  ── Purity & layering ──────────────────────────────────────────────────────
 *  Everything here is a pure, total function over plain numbers. No DB, no
 *  Fastify, no IO, no secrets, no clock, no randomness. @nutians/shared is
 *  bundled into the browser, and the portal's what-if sliders recompute this
 *  client-side over a server-provided baseline seed (ADR-0011 §2.2). It must run
 *  end-to-end on entirely hand-entered values (the all-manual / zero-data mode,
 *  §3.5) — no field is ever required to come from the DB.
 *
 *  The core identities and the §3.1/§3.2 building blocks (blendedCostPerAu,
 *  costPerPayingUser, netFactor, breakEvenListPrice, targetListPrice,
 *  marginAtPrice, sensitivityGrid) live in ./pricing.ts and are reused here
 *  verbatim — this module wires the editable input vector through them and adds
 *  the absolute-money layer (operatingProfit, breakEvenMAU, target deltas).
 *
 *  ── Token mode & MODEL_PRICES (flagged layering decision) ──────────────────
 *  §3.4 token mode derives a per-segment €/AU from `input_tokens` + `output_tokens`
 *  + "a chosen model price". The price table MODEL_PRICES lives in @nutians/ai,
 *  which already imports FROM @nutians/shared (see packages/ai/src/prices.ts) —
 *  so this module CANNOT import it back without a dependency cycle, and importing
 *  a server-only package into a browser bundle would break purity anyway.
 *  Resolution: token mode carries the resolved {@link ModelPrice} VALUE (the
 *  entry the operator picked from MODEL_PRICES upstream), and we derive € via the
 *  in-package {@link computeCostEur}. The portal maps a model name → MODEL_PRICES
 *  and passes the price in. This keeps the module pure and the layering clean.
 *
 *  ── Units (stated on every field; two conventions, by design) ──────────────
 *  - Segment mix is in PERCENT and must sum to 100 (ADR-0011 §3.4 wording).
 *  - Fees / margins / conversion / annual-share-as-fraction are FRACTIONS in
 *    [0, 1] (0.30 = 30%), matching the locked ./pricing.ts convention
 *    (netFactor, costPerPayingUser). Annual-plan SHARE is taken in percent
 *    (0..100) to mirror the mix; everything else is a fraction.
 *  Each field's doc comment states its unit explicitly so the two conventions
 *  never get crossed.
 * ========================================================================== */
/* ----- Sensitivity-grid default axes (ADR-0011 §3.3 #2) -------------------- */
/** Row axis for the sensitivity table: conversion 2/4/6/8/10% (ADR-0011 §3.3). */
export const DEFAULT_SENSITIVITY_CONVERSIONS = [0.02, 0.04, 0.06, 0.08, 0.1];
/** Column axis for the sensitivity table: blended cost/AU €0.30/€0.50/€0.70. */
export const DEFAULT_SENSITIVITY_BLENDED_COSTS = [0.3, 0.5, 0.7];
/* ==========================================================================
 *  Input vector (ADR-0011 §3.4) — every movable field editable
 * ========================================================================== */
/**
 * Population split across the three cost segments, in PERCENT. Must sum to 100
 * (ADR-0011 §3.4 — "must sum to 100"). A segment may be 0 (e.g. an all-light
 * `{light:100, average:0, heavy:0}` single-segment population). This is the
 * editable simulator mix; the actuals-derived percentile bands (the 30/50/20
 * default) live in ./pricing.ts `costBandConfigSchema` (fractions summing to 1).
 */
export const segmentMixSchema = z
    .object({
    light: z.number().min(0).max(100),
    average: z.number().min(0).max(100),
    heavy: z.number().min(0).max(100),
})
    .refine((m) => Math.abs(m.light + m.average + m.heavy - 100) < 1e-9, {
    message: 'segment mix (light + average + heavy) must sum to 100',
});
/** A per-segment monthly token bundle (token mode). Non-negative token counts. */
export const segmentTokensSchema = z.object({
    inputTokens: z.number().nonnegative(),
    outputTokens: z.number().nonnegative(),
});
/**
 * Per-segment AI cost — two interchangeable modes (ADR-0011 §3.4):
 *  - `direct`: mean monthly AI cost €/AU for each segment, typed straight in.
 *  - `tokens`: per-segment monthly input/output tokens + a chosen ModelPrice;
 *    € is derived via {@link computeCostEur}. Lets the operator experiment with
 *    "AI tokens spent" instead of a flat €. The two modes agree exactly when the
 *    tokens→€ derivation equals the direct € (locked by test).
 */
export const aiCostInputSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('direct'),
        /** Mean monthly AI cost €/AU for the light segment. */
        light: z.number().nonnegative(),
        /** Mean monthly AI cost €/AU for the average segment. */
        average: z.number().nonnegative(),
        /** Mean monthly AI cost €/AU for the heavy segment. */
        heavy: z.number().nonnegative(),
    }),
    z.object({
        mode: z.literal('tokens'),
        /** The chosen model's price (€/1M tokens), picked from MODEL_PRICES upstream. */
        modelPrice: modelPriceSchema,
        light: segmentTokensSchema,
        average: segmentTokensSchema,
        heavy: segmentTokensSchema,
    }),
]);
/**
 * Pricing — two interchangeable modes (ADR-0011 §3.4):
 *  - `plan`: monthly list price + annual list price + annual-plan share (%) →
 *    blended ARPU (the §4.5 ARPU↔annual-share tension: more annual lowers ARPU).
 *    The headline "current price" the margin is scored at is the MONTHLY list
 *    price (the §3.3 verdict compares €7.99 directly).
 *  - `arpu`: a direct blended ARPU override. The current price scored is the
 *    ARPU itself (no separate list price in this mode).
 */
export const pricingInputSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('plan'),
        /** Monthly list price €/mo (e.g. 7.99). Also the "current price" for margin. */
        monthlyPriceEur: z.number().nonnegative(),
        /** Annual list price €/yr (e.g. 59 → €4.92/mo equivalent). */
        annualPriceEur: z.number().nonnegative(),
        /** Share of paying users on the annual plan, in PERCENT (0..100). */
        annualSharePct: z.number().min(0).max(100),
    }),
    z.object({
        mode: z.literal('arpu'),
        /** Blended ARPU €/paying-user/mo, typed straight in. */
        arpuEur: z.number().nonnegative(),
    }),
]);
/**
 * Infra cost — two interchangeable modes (ADR-0011 §3.4):
 *  - `total`: total monthly infra € → spread per-AU as total / MAU (0 when MAU=0).
 *  - `perAu`: infra €/AU/mo, typed straight in (the all-manual-friendly form).
 * AI vendor spend is NOT infra here (it is the blended AI cost) — see §4.3.
 */
export const infraInputSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('total'), totalMonthlyEur: z.number().nonnegative() }),
    z.object({ mode: z.literal('perAu'), infraPerAuEur: z.number().nonnegative() }),
]);
/**
 * Fee stack (ADR-0011 §3.4 / §3.2), all FRACTIONS in [0, 1]:
 *  net_factor = (1 − storeFee) · (1 − rcFee) · (1 − paymentFee).
 * storeFee = Apple/Google cut (0.15 small-biz / 0.30 standard); rcFee = RevenueCat
 * (~0.01); paymentFee = any extra processing %. storeFee = 1 ⇒ net_factor = 0.
 */
export const feesInputSchema = z.object({
    /** Store cut as a fraction (0.30 = 30%). */
    storeFee: z.number().min(0).max(1),
    /** RevenueCat fee as a fraction; defaults to DEFAULT_RC_FEE (0.01). */
    rcFee: z.number().min(0).max(1).default(DEFAULT_RC_FEE),
    /** Extra payment/processing fee as a fraction; defaults to 0. */
    paymentFee: z.number().min(0).max(1).default(0),
});
/** Optional override of the §3.3 sensitivity-grid axes. */
export const sensitivityAxesSchema = z.object({
    conversions: z.array(z.number().nonnegative()),
    blendedCosts: z.array(z.number().nonnegative()),
});
/**
 * Vision cost assumptions for the ADR-0014 §6 simulator lever.
 * Optional — present when the operator wants to model vision as a separate
 * cost layer. The baseline endpoint seeds costPerImage from actual
 * extraction-vision ai_usage rows once live; defaults to pixtral-12b estimate.
 */
export const visionAssumptionsSchema = z.object({
    /** Mean €/image from actual vision ai_usage rows, or pixtral-12b estimate. */
    costPerImage: z.number().nonnegative().default(0.0003),
    /** Daily free-tier cap (ADR-0014 §4, default 2). */
    freeImagesPerDay: z.number().int().nonnegative().default(2),
    /** Daily premium-tier cap (ADR-0014 §4, default 10). */
    premiumImagesPerDay: z.number().int().nonnegative().default(10),
    /** Fraction of free users expected to use the shared vision pool (0..1). */
    adoptionRate: z.number().min(0).max(1).default(1),
});
/**
 * Lite chat battery cost assumptions (ADR-0024 §6). Optional and informational:
 * actual Lite chat cost still lands in ai_cost_daily and therefore in blended
 * AI cost once live rollups exist.
 */
export const liteChatAssumptionsSchema = z.object({
    /** Free active users observed in the same reporting window. */
    freeActiveUsers: z.number().int().nonnegative(),
    /** Lite chat turns observed in the window. */
    chatTurns: z.number().int().nonnegative(),
    /** Total Lite chat COGS in EUR from ai_cost_daily. */
    totalCostEur: z.number().nonnegative(),
});
/**
 * Coaching/planning attach assumptions (T15.4 / ADR-0012 §8 WTP).
 *
 * Models how much of Premium conversion is attributable to coaching (algorithmic,
 * ~€0 marginal cost) and what conversion lift is expected from the coaching upsell
 * funnel. Seeded from the T15.3 upsell funnel rates when live data is available.
 */
export const coachingAssumptionsSchema = z.object({
    /** Fraction of premium conversions attributable to coaching upsell path (0..1).
     *  Seeded from coaching_upsell_shown→subscription_started rate when available. */
    coachingAttachPct: z.number().min(0).max(1).default(0),
    /** Expected fractional LIFT on the base conversion rate from coaching presence.
     *  E.g. 0.20 means coaching raises conversion from 5% → 6%. */
    expectedConversionLift: z.number().min(0).default(0),
    /** Marginal monthly €/AU of the non-AI coaching compute (target ~0 — pure math). */
    marginalCostPerCoachingAuEur: z.number().nonnegative().default(0),
});
/**
 * Hypothetical "Performance" mid-tier WTP scenario (T15.4 / ADR-0012 §8).
 *
 * NOT a live SKU — for decision-support only. Lets the operator compare
 * single-Premium (current) vs a split arrangement where a subset of users
 * pay a higher tier price. The delta informs whether a future SKU is worth building.
 */
export const wtpScenarioSchema = z.object({
    /** Hypothetical Performance tier monthly list price €. */
    performancePriceEur: z.number().nonnegative().default(12.99),
    /** Hypothetical Performance annual price € (e.g. 99 → ~€8.25/mo). */
    performanceAnnualPriceEur: z.number().nonnegative().default(99),
    /** Share of Performance subscribers on annual plan %. */
    performanceAnnualSharePct: z.number().min(0).max(100).default(0),
    /** Fraction of current MAU who would subscribe to the Performance tier. Expected
     *  lower than conversionRate (higher price, narrower audience). */
    performanceConversionRate: z.number().min(0).max(1).default(0.02),
});
/** The full §3.4 input vector. `computeEconomics` reads ONLY this — nothing else. */
export const economicsInputSchema = z.object({
    /** Total active users (MAU, trailing 28d — ADR-0011 §4.1). */
    mau: z.number().int().nonnegative(),
    /** Segment population mix in percent (sums to 100). */
    segmentMix: segmentMixSchema,
    /** Per-segment AI cost (direct €/AU or token-derived). */
    aiCost: aiCostInputSchema,
    /** Free→paid conversion as a fraction (0.05 = 5%). Negative/zero → guarded. */
    conversionRate: z.number(),
    /** Pricing (plan blend or direct ARPU). */
    pricing: pricingInputSchema,
    /** Infra cost (total €/mo or €/AU). */
    infra: infraInputSchema,
    /** Store / RevenueCat / payment fees. */
    fees: feesInputSchema,
    /** Optional fixed monthly overhead € (salaries, tooling); default 0. */
    fixedMonthlyOverheadEur: z.number().nonnegative().default(0),
    /** Target gross margin as a fraction (default 0.80, docs/09 §Cost). */
    targetGrossMargin: z.number().default(0.8),
    /** Target monthly operating profit € (drives the above/below verdict); default 0. */
    targetMonthlyOperatingProfitEur: z.number().default(0),
    /** Optional override of the sensitivity-grid axes (defaults to §3.3 axes). */
    sensitivityAxes: sensitivityAxesSchema.optional(),
    /** Vision cost assumptions (ADR-0014 §6). Shown as simulator levers. */
    visionAssumptions: visionAssumptionsSchema.optional(),
    /** Lite chat battery COGS readout (ADR-0024 §6). Informational. */
    liteChatAssumptions: liteChatAssumptionsSchema.optional(),
    /** Coaching/planning attach assumptions (T15.4 / ADR-0012 §8). */
    coachingAssumptions: coachingAssumptionsSchema.optional(),
    /** WTP scenario: hypothetical Performance mid-tier (T15.4 / ADR-0012 §8). */
    wtpScenario: wtpScenarioSchema.optional(),
});
/* ==========================================================================
 *  Output vector (ADR-0011 §3.4 "Outputs")
 * ========================================================================== */
/** Resolved per-segment AI cost €/AU (after any tokens→€ derivation). */
export const segmentCostPerAuSchema = z.object({
    light: z.number().nonnegative(),
    average: z.number().nonnegative(),
    heavy: z.number().nonnegative(),
});
/** One target's above/below readout. `actual`/`delta` are null when undefined. */
export const targetComparisonSchema = z.object({
    /** The configured target value. */
    target: z.number(),
    /** The achieved value, or null when undefined (e.g. margin at conversion = 0). */
    actual: z.number().nullable(),
    /** actual − target, or null when `actual` is null. */
    delta: z.number().nullable(),
    /** True when `actual` is defined and ≥ `target`. */
    meets: z.boolean(),
});
/** The §3.4 above/below indicator for each target. */
export const aboveBelowTargetsSchema = z.object({
    grossMargin: targetComparisonSchema,
    operatingProfit: targetComparisonSchema,
});
/** Everything `computeEconomics` derives from the input vector. */
export const economicsOutputSchema = z.object({
    // ── population ──
    mau: z.number().int().nonnegative(),
    /** round(MAU · conversion). 0 at conversion ≤ 0. */
    payingUsers: z.number().int().nonnegative(),
    conversionRate: z.number(),
    // ── cost ──
    /** Resolved €/AU per segment (post tokens→€). */
    segmentCostPerAu: segmentCostPerAuSchema,
    /** Σ (mix% / 100) · segmentCost — the §3.1 blended cost/AU. */
    blendedCostPerAu: z.number().nonnegative(),
    /** Infra €/AU (total/MAU or as given). */
    infraPerAu: z.number().nonnegative(),
    /** MAU · (blendedCostPerAu + infraPerAu) — every active user incurs COGS. */
    totalCogsEur: z.number().nonnegative(),
    /** (blended + infra) / conversion — true per-paying-user floor; null at conv ≤ 0. */
    costPerPayingUser: z.number().nonnegative().nullable(),
    // ── revenue ──
    /** Blended ARPU €/paying-user (after annual mix), or the ARPU override. */
    blendedArpuEur: z.number().nonnegative(),
    /** The headline list price the margin is scored at (monthly list, or the ARPU). */
    currentPriceEur: z.number().nonnegative(),
    /** Gross monthly recurring revenue: payingUsers · blendedARPU (pre store fee). */
    mrrEur: z.number().nonnegative(),
    /** Net revenue we keep: MRR · net_factor (post store/RC/payment fees). */
    netRevenueEur: z.number().nonnegative(),
    /** (1 − storeFee)·(1 − rcFee)·(1 − paymentFee). */
    netFactor: z.number(),
    // ── pricing decision ──
    /** Post-fee break-even list price; null at conv ≤ 0 or net_factor ≤ 0. */
    breakEvenListPriceEur: z.number().nonnegative().nullable(),
    /** Post-fee list price hitting `targetGrossMargin`; null when unreachable. */
    targetListPriceEur: z.number().nonnegative().nullable(),
    /** Gross margin at `currentPriceEur`, net of fees (negative = loss); null guard. */
    marginAtCurrentPrice: z.number().nullable(),
    /** Gross margin at the blended ARPU (the realised margin); null guard. */
    marginAtArpu: z.number().nullable(),
    // ── profit ──
    fixedMonthlyOverheadEur: z.number().nonnegative(),
    /** netRevenue − totalCOGS − fixed. The absolute monthly operating profit €. */
    operatingProfitEur: z.number(),
    /** MAU at which operating profit = 0 for this conversion/ARPU/fee/fixed; null
     *  when the per-AU contribution ≤ 0 (scaling never breaks even) — see fn doc. */
    breakEvenMau: z.number().nonnegative().nullable(),
    // ── targets ──
    targets: aboveBelowTargetsSchema,
    // ── artifact ──
    sensitivity: sensitivityGridSchema,
    /**
     * Derived vision cost/AU by tier (ADR-0014 §6). Null when visionAssumptions
     * not provided. Informational — does not affect blendedCostPerAu (which
     * already captures vision once ai_cost_daily accumulates extraction-vision rows).
     */
    visionBreakdown: z
        .object({
        costPerImageEur: z.number().nonnegative(),
        freeImagesPerDay: z.number().int().nonnegative(),
        premiumImagesPerDay: z.number().int().nonnegative(),
        /** Observed/assumed vision adoption factor. */
        adoptionRate: z.number().min(0).max(1),
        /** costPerImage × freeImagesPerDay × 30 × adoptionRate */
        freeCostPerAuEur: z.number().nonnegative(),
        /** costPerImage × premiumImagesPerDay × 30 × adoptionRate */
        premiumCostPerAuEur: z.number().nonnegative(),
    })
        .nullable(),
    /**
     * Derived Lite chat COGS/AU from observed battery-era traffic (ADR-0024 §6).
     * Null when liteChatAssumptions is absent. Informational — blendedCostPerAu
     * already includes Lite chat once ai_cost_daily accumulates chat rows.
     */
    liteChatBreakdown: z
        .object({
        freeActiveUsers: z.number().int().nonnegative(),
        chatTurns: z.number().int().nonnegative(),
        totalCostEur: z.number().nonnegative(),
        cogsPerAuEur: z.number().nonnegative().nullable(),
    })
        .nullable(),
    /**
     * Coaching attach breakdown (T15.4 / ADR-0012 §8). Null when
     * coachingAssumptions is absent. Shows the impact of the coaching upsell
     * funnel on conversion and the free:premium funding ratio.
     */
    coachingBreakdown: z
        .object({
        /** Fraction of conversions attributed to coaching upsell path. */
        coachingAttachPct: z.number(),
        /** Fractional conversion lift applied by coaching presence. */
        expectedConversionLift: z.number(),
        /** Base conversion rate × (1 + lift). */
        liftedConversionRate: z.number(),
        /** round(MAU × liftedConversionRate). */
        liftedPayingUsers: z.number().int().nonnegative(),
        /** liftedPayingUsers × blendedARPU. */
        liftedMrrEur: z.number().nonnegative(),
        /** liftedMRR × netFactor. */
        liftedNetRevenueEur: z.number().nonnegative(),
        /** (MAU − payingUsers) / payingUsers — free users per paying user (baseline).
         *  Null when payingUsers = 0 (no conversion). */
        freeToPremiumRatioBase: z.number().nonnegative().nullable(),
        /** (MAU − liftedPayingUsers) / liftedPayingUsers — ratio with coaching lift.
         *  Null when liftedPayingUsers = 0. */
        freeToPremiumRatioLifted: z.number().nonnegative().nullable(),
        /** Total monthly marginal cost of coaching features: MAU × marginalCostPerAU. */
        totalMarginalCostEur: z.number().nonnegative(),
    })
        .nullable(),
    /**
     * WTP scenario breakdown (T15.4 / ADR-0012 §8). Null when wtpScenario absent.
     * Compares single-Premium (current) vs a hypothetical split with a higher-priced
     * "Performance" tier. Informs the future SKU decision — informational only.
     */
    wtpScenarioBreakdown: z
        .object({
        /** Performance tier monthly list price €. */
        performancePriceEur: z.number().nonnegative(),
        /** Blended performance ARPU after annual-plan mix. */
        performanceArpuEur: z.number().nonnegative(),
        /** round(MAU × performanceConversionRate). */
        performancePayingUsers: z.number().int().nonnegative(),
        /** performancePayingUsers × performanceArpu. */
        performanceMrrEur: z.number().nonnegative(),
        /** performanceMRR × netFactor. */
        performanceNetRevenueEur: z.number().nonnegative(),
        /** Single-Premium scenario: current MRR (already in mrrEur). */
        singlePremiumMrrEur: z.number().nonnegative(),
        /** Single-Premium net revenue (already in netRevenueEur). */
        singlePremiumNetRevenueEur: z.number().nonnegative(),
        /** Performance net revenue − single-Premium net revenue. Negative = split hurts. */
        netRevenueDeltaEur: z.number(),
        /** netRevenueDelta / singlePremiumNetRevenue; null when baseline is 0. */
        netRevenueDeltaPct: z.number().nullable(),
    })
        .nullable(),
});
/* ==========================================================================
 *  Pure helpers (the new absolute-money + input-vector layer)
 * ========================================================================== */
/**
 * Derive a segment's monthly €/AU from its token bundle and a model price
 * (ADR-0011 §3.4 token mode). Thin wrapper over {@link computeCostEur} so token
 * mode and direct mode share one code path.
 */
export function segmentCostFromTokens(tokens, price) {
    return computeCostEur(tokens.inputTokens, tokens.outputTokens, price);
}
/**
 * Blend per-segment €/AU by the editable percent mix (ADR-0011 §3.1, generalised
 * to an arbitrary mix instead of the fixed 30/50/20):
 *   blended = (light%·lightCost + avg%·avgCost + heavy%·heavyCost) / 100
 *
 * With the default 30/50/20 mix this is exactly the §3.1 identity. Total: the
 * mix is validated to sum to 100 upstream, so the divisor is always 100.
 */
export function blendSegmentCosts(args) {
    const { mix, costs } = args;
    return (mix.light * costs.light + mix.average * costs.average + mix.heavy * costs.heavy) / 100;
}
/**
 * Blended ARPU from a plan mix (ADR-0011 §4.5):
 *   arpu = (1 − annualShare)·monthlyPrice + annualShare·(annualPrice / 12)
 *
 * annualSharePct is a percent (0..100). At share 0 ⇒ monthlyPrice; at share 100
 * ⇒ the annual monthly-equivalent (annualPrice/12 — €59/yr → €4.92/mo). Captures
 * the §4.5 tension: more annual share *lowers* blended ARPU.
 */
export function blendedArpuFromPlan(args) {
    const share = args.annualSharePct / 100;
    const annualMonthlyEquiv = args.annualPriceEur / 12;
    return (1 - share) * args.monthlyPriceEur + share * annualMonthlyEquiv;
}
/**
 * Absolute monthly operating profit (ADR-0011 §3.4):
 *   operating_profit = revenue − COGS − fixed
 *
 * Pure subtraction — `revenue` is expected to already be NET of store/RC/payment
 * fees (the honest figure; computeEconomics passes net revenue). May be negative.
 */
export function operatingProfit(args) {
    return args.revenue - args.cogs - args.fixedOverhead;
}
/**
 * The MAU at which operating profit hits zero for a given conversion, ARPU, fee
 * stack and fixed overhead (ADR-0011 §3.4 "break-even MAU"). Solving
 *   MAU·conversion·arpu·net_factor − MAU·(blended + infra) − fixed = 0
 *   ⇒ MAU = fixed / contributionPerAu,
 *     where contributionPerAu = conversion·arpu·net_factor − (blended + infra)
 * is the net operating contribution each active user adds.
 *
 * Cases (all total, no Infinity, no div-by-zero):
 *  - contributionPerAu > 0, fixed > 0 → finite positive MAU.
 *  - contributionPerAu > 0, fixed ≤ 0 → 0 (already profitable at any positive scale).
 *  - contributionPerAu = 0           → 0 if fixed ≤ 0 (flat at break-even),
 *                                       else null (a flat negative line never recovers).
 *  - contributionPerAu < 0           → null (every added user deepens the loss;
 *                                       scaling NEVER breaks even).
 *
 * FORMULA NOTE / flagged ambiguity (do not silently guess): the static model in
 * docs/09 §"Unit economics" says the product can "scale past 25k+ active users to
 * be sustainable at this pricing". That is only true when contributionPerAu > 0
 * (per-paying-user revenue clears per-user COGS). At its own stated numbers
 * (conv ≈ 4.76%, ARPU €7.50, blended+infra €0.50, no fee) the contribution is
 * NEGATIVE (0.0476·7.5 − 0.50 ≈ −€0.143/AU), so by this rigorous formula scaling
 * can NEVER break even — only raising conversion (or ARPU/price, or cutting cost)
 * can. The two doc "levers" therefore collapse to one (improve per-user unit
 * economics); the "scale to 25k" lever implicitly assumes the higher (≥7–8%)
 * conversion the doc also names. We implement the rigorous contribution formula
 * and return null in the negative-contribution case rather than fabricate a
 * finite break-even scale.
 */
export function breakEvenMAU(args) {
    const contributionPerAu = args.conversionRate * args.arpu * args.netFactor - (args.blendedCostPerAu + args.infraPerAu);
    if (args.fixedOverhead <= 0) {
        // No fixed cost to amortise: profitable (or flat) at any scale ⇒ break even at 0.
        return contributionPerAu >= 0 ? 0 : null;
    }
    // Fixed cost present: only a strictly positive contribution can ever cover it.
    if (contributionPerAu <= 0)
        return null;
    return args.fixedOverhead / contributionPerAu;
}
/**
 * The §3.4 above/below readout: compare achieved gross margin and operating
 * profit against their targets. `meets` is true only when the achieved value is
 * defined and ≥ target; `delta` is achieved − target (null when margin is null,
 * e.g. at conversion = 0). Operating profit is always defined, so its `actual`
 * is never null.
 */
export function aboveBelowTargets(args) {
    const { marginAtPrice: margin, targetMargin, operatingProfit: profit, targetOperatingProfit, } = args;
    return {
        grossMargin: {
            target: targetMargin,
            actual: margin,
            delta: margin === null ? null : margin - targetMargin,
            meets: margin !== null && margin >= targetMargin,
        },
        operatingProfit: {
            target: targetOperatingProfit,
            actual: profit,
            delta: profit - targetOperatingProfit,
            meets: profit >= targetOperatingProfit,
        },
    };
}
/* ==========================================================================
 *  computeEconomics — the headline pure function
 * ========================================================================== */
/**
 * The locked §3.4 contract: a pure, total, IO-free function from the full input
 * vector to every derived output. Parsing rejects an invalid vector (e.g. a
 * segment mix that does not sum to 100 — ADR-0011 §3.4); all downstream math is
 * total (conversion ≤ 0, net_factor ≤ 0, zero MAU and zero price are guarded to
 * null, never NaN/Infinity). Generalises the static docs/09 §"Unit economics"
 * model and reconciles its −€1,500/mo worked example exactly (see tests).
 */
export function computeEconomics(input) {
    const parsed = economicsInputSchema.parse(input);
    // 1. Resolve per-segment €/AU (token mode → derived €; direct mode → as typed).
    const segmentCostPerAu = parsed.aiCost.mode === 'tokens'
        ? {
            light: segmentCostFromTokens(parsed.aiCost.light, parsed.aiCost.modelPrice),
            average: segmentCostFromTokens(parsed.aiCost.average, parsed.aiCost.modelPrice),
            heavy: segmentCostFromTokens(parsed.aiCost.heavy, parsed.aiCost.modelPrice),
        }
        : { light: parsed.aiCost.light, average: parsed.aiCost.average, heavy: parsed.aiCost.heavy };
    // 2. Blend by the editable mix (§3.1 identity, arbitrary mix).
    const blendedCostPerAu = blendSegmentCosts({ mix: parsed.segmentMix, costs: segmentCostPerAu });
    // 3. Resolve infra €/AU (total/MAU guarded at MAU = 0).
    const infraPerAu = parsed.infra.mode === 'total'
        ? parsed.mau > 0
            ? parsed.infra.totalMonthlyEur / parsed.mau
            : 0
        : parsed.infra.infraPerAuEur;
    // 4. Resolve blended ARPU and the headline current price.
    const blendedArpuEur = parsed.pricing.mode === 'plan'
        ? blendedArpuFromPlan({
            monthlyPriceEur: parsed.pricing.monthlyPriceEur,
            annualPriceEur: parsed.pricing.annualPriceEur,
            annualSharePct: parsed.pricing.annualSharePct,
        })
        : parsed.pricing.arpuEur;
    const currentPriceEur = parsed.pricing.mode === 'plan' ? parsed.pricing.monthlyPriceEur : parsed.pricing.arpuEur;
    // 5. Fee stack → net factor (store · rc · payment).
    const nf = netFactor({ storeFee: parsed.fees.storeFee, rcFee: parsed.fees.rcFee }) *
        (1 - parsed.fees.paymentFee);
    // 6. §3.1/§3.2 core identities (reused from ./pricing.ts).
    const cost = costPerPayingUser({
        blendedCostPerAu,
        infraPerAu,
        conversionRate: parsed.conversionRate,
    });
    const breakEvenListPriceEur = breakEvenListPrice({ costPerPayingUser: cost, netFactor: nf });
    const targetListPriceEur = targetListPrice({
        costPerPayingUser: cost,
        netFactor: nf,
        margin: parsed.targetGrossMargin,
    });
    const marginAtCurrentPrice = marginAtPrice({
        listPrice: currentPriceEur,
        costPerPayingUser: cost,
        netFactor: nf,
    });
    const marginAtArpu = marginAtPrice({
        listPrice: blendedArpuEur,
        costPerPayingUser: cost,
        netFactor: nf,
    });
    // 7. Absolute-money layer.
    const payingUsers = parsed.conversionRate > 0 ? Math.round(parsed.mau * parsed.conversionRate) : 0;
    const totalCogsEur = parsed.mau * (blendedCostPerAu + infraPerAu);
    const mrrEur = payingUsers * blendedArpuEur;
    const netRevenueEur = mrrEur * nf;
    const operatingProfitEur = operatingProfit({
        revenue: netRevenueEur,
        cogs: totalCogsEur,
        fixedOverhead: parsed.fixedMonthlyOverheadEur,
    });
    const breakEvenMau = breakEvenMAU({
        conversionRate: parsed.conversionRate,
        arpu: blendedArpuEur,
        netFactor: nf,
        blendedCostPerAu,
        infraPerAu,
        fixedOverhead: parsed.fixedMonthlyOverheadEur,
    });
    // 8. Targets readout (§3.4 above/below). Margin scored at the headline price.
    const targets = aboveBelowTargets({
        marginAtPrice: marginAtCurrentPrice,
        targetMargin: parsed.targetGrossMargin,
        operatingProfit: operatingProfitEur,
        targetOperatingProfit: parsed.targetMonthlyOperatingProfitEur,
    });
    // 9. Vision breakdown (ADR-0014 §6) — informational, does not affect core math.
    const visionBreakdown = parsed.visionAssumptions
        ? {
            costPerImageEur: parsed.visionAssumptions.costPerImage,
            freeImagesPerDay: parsed.visionAssumptions.freeImagesPerDay,
            premiumImagesPerDay: parsed.visionAssumptions.premiumImagesPerDay,
            adoptionRate: parsed.visionAssumptions.adoptionRate,
            freeCostPerAuEur: parsed.visionAssumptions.costPerImage *
                parsed.visionAssumptions.freeImagesPerDay *
                30 *
                parsed.visionAssumptions.adoptionRate,
            premiumCostPerAuEur: parsed.visionAssumptions.costPerImage *
                parsed.visionAssumptions.premiumImagesPerDay *
                30 *
                parsed.visionAssumptions.adoptionRate,
        }
        : null;
    // 10. Lite chat battery cost readout (ADR-0024 §6) — informational.
    const liteChatBreakdown = parsed.liteChatAssumptions
        ? {
            freeActiveUsers: parsed.liteChatAssumptions.freeActiveUsers,
            chatTurns: parsed.liteChatAssumptions.chatTurns,
            totalCostEur: parsed.liteChatAssumptions.totalCostEur,
            cogsPerAuEur: parsed.liteChatAssumptions.freeActiveUsers > 0
                ? parsed.liteChatAssumptions.totalCostEur / parsed.liteChatAssumptions.freeActiveUsers
                : null,
        }
        : null;
    // 11. Sensitivity grid artifact (§3.3 #2), against the headline current price.
    const axes = parsed.sensitivityAxes ?? {
        conversions: [...DEFAULT_SENSITIVITY_CONVERSIONS],
        blendedCosts: [...DEFAULT_SENSITIVITY_BLENDED_COSTS],
    };
    const sensitivity = sensitivityGrid(axes.conversions, axes.blendedCosts, {
        infraPerAu,
        netFactor: nf,
        currentPrice: currentPriceEur,
    });
    // 12. Coaching attach breakdown (T15.4 / ADR-0012 §8) — informational.
    const coachingBreakdown = parsed.coachingAssumptions
        ? (() => {
            const ca = parsed.coachingAssumptions;
            const liftedConversionRate = parsed.conversionRate * (1 + ca.expectedConversionLift);
            const liftedPayingUsers = liftedConversionRate > 0 ? Math.round(parsed.mau * liftedConversionRate) : 0;
            const liftedMrrEur = liftedPayingUsers * blendedArpuEur;
            const liftedNetRevenueEur = liftedMrrEur * nf;
            const freeToPremiumRatioBase = payingUsers > 0 ? (parsed.mau - payingUsers) / payingUsers : null;
            const freeToPremiumRatioLifted = liftedPayingUsers > 0 ? (parsed.mau - liftedPayingUsers) / liftedPayingUsers : null;
            return {
                coachingAttachPct: ca.coachingAttachPct,
                expectedConversionLift: ca.expectedConversionLift,
                liftedConversionRate,
                liftedPayingUsers,
                liftedMrrEur,
                liftedNetRevenueEur,
                freeToPremiumRatioBase,
                freeToPremiumRatioLifted,
                totalMarginalCostEur: parsed.mau * ca.marginalCostPerCoachingAuEur,
            };
        })()
        : null;
    // 13. WTP scenario breakdown (T15.4 / ADR-0012 §8) — informational, not a live SKU.
    const wtpScenarioBreakdown = parsed.wtpScenario
        ? (() => {
            const wtp = parsed.wtpScenario;
            const performanceArpuEur = blendedArpuFromPlan({
                monthlyPriceEur: wtp.performancePriceEur,
                annualPriceEur: wtp.performanceAnnualPriceEur,
                annualSharePct: wtp.performanceAnnualSharePct,
            });
            const performancePayingUsers = wtp.performanceConversionRate > 0
                ? Math.round(parsed.mau * wtp.performanceConversionRate)
                : 0;
            const performanceMrrEur = performancePayingUsers * performanceArpuEur;
            const performanceNetRevenueEur = performanceMrrEur * nf;
            const netRevenueDeltaEur = performanceNetRevenueEur - netRevenueEur;
            return {
                performancePriceEur: wtp.performancePriceEur,
                performanceArpuEur,
                performancePayingUsers,
                performanceMrrEur,
                performanceNetRevenueEur,
                singlePremiumMrrEur: mrrEur,
                singlePremiumNetRevenueEur: netRevenueEur,
                netRevenueDeltaEur,
                netRevenueDeltaPct: netRevenueEur > 0 ? netRevenueDeltaEur / netRevenueEur : null,
            };
        })()
        : null;
    return {
        mau: parsed.mau,
        payingUsers,
        conversionRate: parsed.conversionRate,
        segmentCostPerAu,
        blendedCostPerAu,
        infraPerAu,
        totalCogsEur,
        costPerPayingUser: cost,
        blendedArpuEur,
        currentPriceEur,
        mrrEur,
        netRevenueEur,
        netFactor: nf,
        breakEvenListPriceEur,
        targetListPriceEur,
        marginAtCurrentPrice,
        marginAtArpu,
        fixedMonthlyOverheadEur: parsed.fixedMonthlyOverheadEur,
        operatingProfitEur,
        breakEvenMau,
        targets,
        sensitivity,
        visionBreakdown,
        liteChatBreakdown,
        coachingBreakdown,
        wtpScenarioBreakdown,
    };
}
//# sourceMappingURL=economics.js.map